import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCurrency } from '../../contexts/CurrencyContext';
import { 
  FiCreditCard, 
  FiTrendingUp, 
  FiFileText, 
  FiUsers, 
  FiPlus,
  FiEye,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiArrowUp,
  FiArrowDown,
  FiFilter,
  FiStar
} from 'react-icons/fi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import styles from './Dashboard.module.css';

// Enhanced mock data for development
const monthlyData = [
  { month: 'Jan', revenue: 45000, expenses: 32000, profit: 13000, invoices: 12, quotes: 8 },
  { month: 'Feb', revenue: 52000, expenses: 35000, profit: 17000, invoices: 15, quotes: 10 },
  { month: 'Mar', revenue: 48000, expenses: 33000, profit: 15000, invoices: 13, quotes: 9 },
  { month: 'Apr', revenue: 61000, expenses: 38000, profit: 23000, invoices: 18, quotes: 12 },
  { month: 'May', revenue: 55000, expenses: 36000, profit: 19000, invoices: 16, quotes: 11 },
  { month: 'Jun', revenue: 67000, expenses: 42000, profit: 25000, invoices: 20, quotes: 14 },
];

const recentInvoices = [
  {
    id: '1',
    invoiceNumber: 'INV-2024-001',
    clientName: 'Acme Corporation',
    amount: 2500,
    status: 'paid',
    dueDate: '2024-01-15',
    paidDate: '2024-01-10'
  },
  {
    id: '2',
    invoiceNumber: 'INV-2024-002',
    clientName: 'Tech Solutions Inc',
    amount: 1800,
    status: 'sent',
    dueDate: '2024-01-20'
  },
  {
    id: '3',
    invoiceNumber: 'INV-2024-003',
    clientName: 'Global Industries',
    amount: 3200,
    status: 'overdue',
    dueDate: '2024-01-10'
  },
  {
    id: '4',
    invoiceNumber: 'INV-2024-004',
    clientName: 'Innovation Labs',
    amount: 4500,
    status: 'draft',
    dueDate: '2024-02-01'
  }
];

const recentQuotes = [
  {
    id: '1',
    quoteNumber: 'Q-2024-001',
    clientName: 'Startup Innovations',
    amount: 8000,
    status: 'sent',
    validUntil: '2024-02-15',
    daysLeft: 12
  },
  {
    id: '2',
    quoteNumber: 'Q-2024-002',
    clientName: 'TechCorp Solutions',
    amount: 12000,
    status: 'accepted',
    validUntil: '2024-02-10',
    daysLeft: 7
  },
  {
    id: '3',
    quoteNumber: 'Q-2024-003',
    clientName: 'Global Enterprises',
    amount: 15000,
    status: 'expired',
    validUntil: '2024-01-25',
    daysLeft: -5
  }
];

const topClients = [
  { id: '1', name: 'Acme Corporation', revenue: 45000, invoices: 12, growth: 15.2 },
  { id: '2', name: 'Tech Solutions Inc', revenue: 38000, invoices: 8, growth: 8.7 },
  { id: '3', name: 'Global Industries', revenue: 32000, invoices: 6, growth: -2.1 },
  { id: '4', name: 'Innovation Labs', revenue: 28000, invoices: 5, growth: 22.5 }
];

const recentActivity = [
  {
    id: '1',
    type: 'invoice_paid',
    message: 'Invoice INV-2024-001 paid by Acme Corporation',
    amount: 2500,
    timestamp: new Date('2024-01-10T14:30:00'),
    status: 'success'
  },
  {
    id: '2',
    type: 'quote_sent',
    message: 'Quote Q-2024-002 sent to TechCorp Solutions',
    amount: 12000,
    timestamp: new Date('2024-01-10T11:15:00'),
    status: 'info'
  },
  {
    id: '3',
    type: 'invoice_overdue',
    message: 'Invoice INV-2024-003 overdue from Global Industries',
    amount: 3200,
    timestamp: new Date('2024-01-10T09:45:00'),
    status: 'warning'
  },
  {
    id: '4',
    type: 'client_added',
    message: 'New client "Startup Innovations" added',
    timestamp: new Date('2024-01-09T16:20:00'),
    status: 'success'
  }
];

const statusDistribution = [
  { name: 'Paid', value: 65, color: '#10b981' },
  { name: 'Sent', value: 20, color: '#3b82f6' },
  { name: 'Overdue', value: 10, color: '#f59e0b' },
  { name: 'Draft', value: 5, color: '#6b7280' }
];

export function Dashboard() {
  const [selectedPeriod, setSelectedPeriod] = useState('6months');
  const [selectedMetric, setSelectedMetric] = useState('revenue');
  const { formatCurrency: formatCurrencyFromContext } = useCurrency();

  const totalRevenue = 328000;
  const totalExpenses = 216000;
  const totalProfit = totalRevenue - totalExpenses;
  const outstandingPayments = 45000;
  const overdueInvoices = 3;

  const revenueGrowth = 12.5;
  const profitGrowth = 18.7;

  // Calculate additional metrics
  const conversionRate = (65 / 100) * 100; // Paid vs total
  const daysOutstanding = 28; // Average days to payment

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return styles.statusPaid;
      case 'sent': return styles.statusSent;
      case 'overdue': return styles.statusOverdue;
      case 'draft': return styles.statusDraft;
      case 'accepted': return styles.statusAccepted;
      case 'expired': return styles.statusExpired;
      default: return styles.statusDraft;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'paid': return 'Paid';
      case 'sent': return 'Sent';
      case 'overdue': return 'Overdue';
      case 'draft': return 'Draft';
      case 'accepted': return 'Accepted';
      case 'expired': return 'Expired';
      default: return 'Draft';
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'invoice_paid': return <FiCheckCircle size={16} />;
      case 'quote_sent': return <FiFileText size={16} />;
      case 'invoice_overdue': return <FiAlertCircle size={16} />;
      case 'client_added': return <FiUsers size={16} />;
      default: return <FiFileText size={16} />;
    }
  };

  const getActivityStatusClass = (status: string) => {
    switch (status) {
      case 'success': return styles.activitySuccess;
      case 'warning': return styles.activityWarning;
      case 'info': return styles.activityInfo;
      default: return styles.activityInfo;
    }
  };

  const formatCurrency = formatCurrencyFromContext;

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <div className={styles.dashboard}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Dashboard</h1>
          <p className={styles.subtitle}>Welcome back! Here's what's happening with your business.</p>
        </div>
        <div className={styles.actions}>
          <Link to="/quotes/create" className={styles.createButton}>
            <FiPlus size={16} />
            New Quote
          </Link>
          <Link to="/invoices/create" className={styles.createButton}>
            <FiPlus size={16} />
            New Invoice
          </Link>
          <Link to="/settings/subscription" className={styles.subscriptionButton}>
            <FiStar size={16} />
            View Plans
          </Link>
        </div>
      </div>

      {/* Enhanced Stats Cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FiCreditCard size={24} />
          </div>
          <div className={styles.statContent}>
            <h3 className={styles.statLabel}>Total Revenue</h3>
            <p className={styles.statValue}>{formatCurrency(totalRevenue)}</p>
            <div className={`${styles.statChange} ${revenueGrowth > 0 ? styles.positive : styles.negative}`}>
              {revenueGrowth > 0 ? <FiArrowUp size={16} /> : <FiArrowDown size={16} />}
              <span>{Math.abs(revenueGrowth)}% from last month</span>
            </div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FiTrendingUp size={24} />
          </div>
          <div className={styles.statContent}>
            <h3 className={styles.statLabel}>Total Profit</h3>
            <p className={styles.statValue}>{formatCurrency(totalProfit)}</p>
            <div className={`${styles.statChange} ${profitGrowth > 0 ? styles.positive : styles.negative}`}>
              <FiArrowUp size={16} />
              <span>{profitGrowth}% from last month</span>
            </div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FiFileText size={24} />
          </div>
          <div className={styles.statContent}>
            <h3 className={styles.statLabel}>Outstanding Payments</h3>
            <p className={styles.statValue}>{formatCurrency(outstandingPayments)}</p>
            <div className={styles.statChange}>
              <span>{overdueInvoices} overdue invoices</span>
            </div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FiUsers size={24} />
          </div>
          <div className={styles.statContent}>
            <h3 className={styles.statLabel}>Active Clients</h3>
            <p className={styles.statValue}>24</p>
            <div className={`${styles.statChange} ${styles.positive}`}>
              <FiArrowUp size={16} />
              <span>+3 this month</span>
            </div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FiClock size={24} />
          </div>
          <div className={styles.statContent}>
            <h3 className={styles.statLabel}>Avg. Days to Pay</h3>
            <p className={styles.statValue}>{daysOutstanding}</p>
            <div className={`${styles.statChange} ${styles.negative}`}>
              <FiArrowDown size={16} />
              <span>-2 days from last month</span>
            </div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FiCheckCircle size={24} />
          </div>
          <div className={styles.statContent}>
            <h3 className={styles.statLabel}>Conversion Rate</h3>
            <p className={styles.statValue}>{conversionRate}%</p>
            <div className={`${styles.statChange} ${styles.positive}`}>
              <FiArrowUp size={16} />
              <span>+5% from last month</span>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Charts and Tables */}
      <div className={styles.contentGrid}>
        {/* Revenue Chart with Period Selector */}
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3 className={styles.chartTitle}>Revenue vs Expenses</h3>
            <div className={styles.chartControls}>
              <select 
                className={styles.chartSelect}
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
              >
                <option value="6months">Last 6 months</option>
                <option value="1year">Last year</option>
                <option value="2years">Last 2 years</option>
              </select>
              <select 
                className={styles.chartSelect}
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
              >
                <option value="revenue">Revenue</option>
                <option value="profit">Profit</option>
                <option value="invoices">Invoices</option>
              </select>
            </div>
          </div>
          <div className={styles.chartContainer}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey={selectedMetric === 'revenue' ? 'revenue' : selectedMetric === 'profit' ? 'profit' : 'invoices'}
                  stroke="#3b82f6" 
                  strokeWidth={3}
                  name={selectedMetric === 'revenue' ? 'Revenue' : selectedMetric === 'profit' ? 'Profit' : 'Invoices'}
                  dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
                />
                {selectedMetric === 'revenue' && (
                  <Line 
                    type="monotone" 
                    dataKey="expenses" 
                    stroke="#ef4444" 
                    strokeWidth={2}
                    name="Expenses"
                    dot={{ fill: '#ef4444', strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5, stroke: '#ef4444', strokeWidth: 2 }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status Distribution Pie Chart */}
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3 className={styles.chartTitle}>Invoice Status Distribution</h3>
          </div>
          <div className={styles.chartContainer}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className={styles.pieChartLegend}>
              {statusDistribution.map((item, index) => (
                <div key={index} className={styles.legendItem}>
                  <div className={styles.legendColor} style={{ backgroundColor: item.color }}></div>
                  <span>{item.name}</span>
                  <span className={styles.legendValue}>{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Invoices */}
        <div className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <h3 className={styles.tableTitle}>Recent Invoices</h3>
            <Link to="/invoices" className={styles.viewAllLink}>
              View all
            </Link>
          </div>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Client</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Due Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recentInvoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{invoice.invoiceNumber}</td>
                    <td>{invoice.clientName}</td>
                    <td>{formatCurrency(invoice.amount)}</td>
                    <td>
                      <span className={`${styles.status} ${getStatusColor(invoice.status)}`}>
                        {getStatusText(invoice.status)}
                      </span>
                    </td>
                    <td>
                      <div className={styles.dateInfo}>
                        <span>{new Date(invoice.dueDate).toLocaleDateString()}</span>
                        {invoice.status === 'paid' && invoice.paidDate && (
                          <span className={styles.paidDate}>
                            Paid: {new Date(invoice.paidDate).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <Link to={`/invoices/${invoice.id}`} className={styles.viewButton}>
                        <FiEye size={16} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Quotes */}
        <div className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <h3 className={styles.tableTitle}>Recent Quotes</h3>
            <Link to="/quotes" className={styles.viewAllLink}>
              View all
            </Link>
          </div>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Quote</th>
                  <th>Client</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Valid Until</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recentQuotes.map((quote) => (
                  <tr key={quote.id}>
                    <td>{quote.quoteNumber}</td>
                    <td>{quote.clientName}</td>
                    <td>{formatCurrency(quote.amount)}</td>
                    <td>
                      <span className={`${styles.status} ${getStatusColor(quote.status)}`}>
                        {getStatusText(quote.status)}
                      </span>
                    </td>
                    <td>
                      <div className={styles.dateInfo}>
                        <span>{new Date(quote.validUntil).toLocaleDateString()}</span>
                        {quote.daysLeft > 0 && (
                          <span className={styles.daysLeft}>
                            {quote.daysLeft} days left
                          </span>
                        )}
                        {quote.daysLeft < 0 && (
                          <span className={styles.expiredDays}>
                            Expired {Math.abs(quote.daysLeft)} days ago
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <Link to={`/quotes/${quote.id}`} className={styles.viewButton}>
                        <FiEye size={16} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Clients */}
        <div className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <h3 className={styles.tableTitle}>Top Clients</h3>
            <Link to="/clients" className={styles.viewAllLink}>
              View all
            </Link>
          </div>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Revenue</th>
                  <th>Invoices</th>
                  <th>Growth</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {topClients.map((client) => (
                  <tr key={client.id}>
                    <td>{client.name}</td>
                    <td>{formatCurrency(client.revenue)}</td>
                    <td>{client.invoices}</td>
                    <td>
                      <span className={`${styles.growth} ${client.growth > 0 ? styles.positive : styles.negative}`}>
                        {client.growth > 0 ? '+' : ''}{client.growth}%
                      </span>
                    </td>
                    <td>
                      <Link to={`/clients/${client.id}`} className={styles.viewButton}>
                        <FiEye size={16} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Activity Feed */}
        <div className={styles.activityCard}>
          <div className={styles.activityHeader}>
            <h3 className={styles.activityTitle}>Recent Activity</h3>
            <button className={styles.filterButton}>
              <FiFilter size={16} />
              Filter
            </button>
          </div>
          <div className={styles.activityList}>
            {recentActivity.map((activity) => (
              <div key={activity.id} className={`${styles.activityItem} ${getActivityStatusClass(activity.status)}`}>
                <div className={styles.activityIcon}>
                  {getActivityIcon(activity.type)}
                </div>
                <div className={styles.activityContent}>
                  <p className={styles.activityMessage}>{activity.message}</p>
                  {activity.amount && (
                    <span className={styles.activityAmount}>{formatCurrency(activity.amount)}</span>
                  )}
                  <span className={styles.activityTime}>{formatDate(activity.timestamp)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
